import { Station } from '../types/stationTypes'
import { Passenger, PassengerType } from '../types/passengerTypes'
import { CarriageLayout } from '../types/bookingTypes'
import { getToken } from './tokenService'
import { useBookingStore } from '../stores/bookingStore'
import { useErrorStore } from '../stores/errorStore'
import { getDefaultHeaders } from '../utils/languageUtils'
import { format, addDays } from 'date-fns'
import { BookingData } from '../types/bookingTypes'
import { useUserStore } from '../stores/userStore'
import { useAuthStore } from '../stores/oauthStore'
import { useJourneyStore } from '../stores/journeyStore'
import { useMyTravelStore } from '../stores/myTravelStore'
import { clearProvisionalBookingInfo } from './storageService'

//TODO splitt this up
const BASE_URL = import.meta.env.VITE_API_BASE_URL
interface BookingSegment {
  id: string
  origin: string
  origin_name: string
  destination: string
  destination_name: string
  direction: 'outbound' | 'inbound'
  service_name: string
  service_identifier: string
  start_validity_date: string
  service_type: string
  items: Array<{
    passenger_id: string
    tariff_code: string
  }>
}

interface PaymentData {
  booking_number: string
  payment_method_code: string
  return_url?: string
  psp_payment_details?: {
    channel: 'Web' | 'iOS' | 'Android'
    telephone_number?: string
  }
}

interface Travel {
  origin: string
  destination: string
  direction: 'outbound' | 'inbound'
  departure: string
  product_types: string[]
}

interface SearchParams {
  origin: any
  destination: any
  outboundDate: string
  returnDate: string | null
  passengers: Passenger[]
}

/**
 * Ensures that we have a valid booking token before making critical booking-related API calls
 * This helps prevent authentication errors when tokens expire during the rebooking process
 * @param bookingNumber The booking number for which we need a valid token
 * @returns A valid booking token
 */
export const ensureValidBookingToken = async (
  bookingNumber: string
): Promise<string> => {
  const authStore = useAuthStore()

  // Check if we have a valid booking token for this booking number
  if (
    authStore.isBookingToken &&
    authStore.currentBookingNumber === bookingNumber &&
    authStore.tokenExpirationTime &&
    Date.now() < authStore.tokenExpirationTime - 5 * 60 * 1000 // 5 min buffer
  ) {
    // Token is still valid, return it
    return authStore.token as string
  }

  // We need to refresh the token
  const refreshed = await authStore.refreshBookingToken()
  if (refreshed) {
    return authStore.token as string
  }

  throw new Error('Session expired: Unable to refresh booking token')
}

export const performJourneySearch = async (params: SearchParams) => {
  const journeyStore = useJourneyStore()
  const errorStore = useErrorStore()
  const myTravelStore = useMyTravelStore()

  // Validate station codes
  if (
    !params.origin._u_i_c_station_code ||
    !params.destination._u_i_c_station_code
  ) {
    const error = 'Invalid station codes'
    errorStore.setError(error)
    throw new Error(error)
  }

  const travels: Travel[] = [
    {
      origin: params.origin._u_i_c_station_code,
      destination: params.destination._u_i_c_station_code,
      direction: 'outbound',
      departure: params.outboundDate,
      product_types: ['ST', 'TO'],
    },
  ]

  if (params.returnDate) {
    travels.push({
      origin: params.destination._u_i_c_station_code,
      destination: params.origin._u_i_c_station_code,
      direction: 'inbound',
      departure: params.returnDate,
      product_types: ['ST', 'TO'],
    })
  }

  try {
    myTravelStore.setDates('', null)
    myTravelStore.setOutboundTravel(null)
    myTravelStore.setInboundTravel(null)
    myTravelStore.updatePassengerCounts('outbound', [], [])
    myTravelStore.updatePassengerCounts('inbound', [], [])
    myTravelStore.setHasReturnTravel(!!params.returnDate)

    const response = await postJourneySearch({
      travels,
      passengers: params.passengers,
      currency: 'NOK',
    })
    if (response?.data?.offer?.travels?.length > 0) {
      journeyStore.setJourneySearchResults(response)
      journeyStore.setPassengers(params.passengers)
      return response
    } else {
      errorStore.setError('No travels found')
      throw new Error('No travels found')
    }
  } catch (error: any) {
    errorStore.setError(error.message)
    throw error
  }
}

export const fetchAllStations = async (): Promise<Station[]> => {
  return fetchStations()
}

export const fetchStations = async (): Promise<Station[]> => {
  const errorStore = useErrorStore()
  const token = await getToken()
  const headers = await getDefaultHeaders(token)

  const url = `${BASE_URL}/api/v2/meta/stations`

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const error = 'Kunne ikke hente stasjoner.'
      errorStore.setError(error)
      throw new Error(error)
    }

    const data: Station[] = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching stations:', error)
    throw error
  }
}

export const fetchDestinationSuggestions = async (uicStationCode: string) => {
  const errorStore = useErrorStore()
  const token = await getToken()
  const headers = await getDefaultHeaders(token)
  const url = `${BASE_URL}/api/v2/meta/stations/${uicStationCode}`

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const error = 'Kunne ikke hente destinasjonsforslag.'
      errorStore.setError(error)
      throw new Error(error)
    }

    const data = await response.json()
    return data.connected_stations
  } catch (error) {
    console.error('Error fetching destination suggestions:', error)
    throw error
  }
}

export const fetchPassengerTypes = async (): Promise<PassengerType[]> => {
  const errorStore = useErrorStore()
  const token = await getToken()
  const headers = await getDefaultHeaders(token)
  const url = `${BASE_URL}/api/v3/fare/passenger-types`

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const error = 'Kunne ikke hente passasjertyper.'
      errorStore.setError(error)
      throw new Error(error)
    }

    const data = await response.json()
    return data.passenger_types
  } catch (error) {
    console.error('Error fetching passenger types:', error)
    throw error
  }
}

export const postJourneySearch = async (body: any): Promise<any> => {
  const errorStore = useErrorStore()
  const token = await getToken()
  const headers = await getDefaultHeaders(token)
  const url = `${BASE_URL}/api/v2/orientation/journey-search`

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      let errorData
      try {
        errorData = await response.json()
      } catch (e) {
        errorData = {
          message: `HTTP ${response.status}: ${response.statusText}`,
        }
      }
      console.error(
        'postJourneySearch error response:',
        JSON.stringify(errorData, null, 2)
      )
      const errorMessage =
        errorData.errors?.[0]?.message ||
        errorData.message ||
        errorData.error?.message ||
        'Kunne ikke hente reiseresultater.'
      errorStore.setError(errorMessage)
      throw new Error(errorMessage)
    }

    return await response.json()
  } catch (error) {
    console.error('Error fetching journey search results:', error)
    throw error
  }
}

export const bookJourney = async () => {
  const bookingStore = useBookingStore()
  const authStore = useAuthStore()
  const userStore = useUserStore()

  // For logged-in users, use their CRM token to ensure booking is connected to their account
  // For non-logged-in users, get a public token
  const token = await getToken(false)
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v2/booking`

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(bookingStore.getBookingRequestData()),
    })
    if (!response.ok) {
      throw new Error('Could not create booking')
    }

    let data
    try {
      data = await response.json()
      if (data?.data?.booking?.booking_number) {
        const bookingNumber = data.data.booking.booking_number
        localStorage.setItem('token', token)
        localStorage.setItem('pendingBooking', 'true')
        localStorage.setItem('pendingBookingNumber', bookingNumber)
        bookingStore.setBookingNumber(bookingNumber)
        if (token) {
          const expirationTime = Date.now() + 20 * 60 * 1000

          const tokenType = userStore.isLoggedIn ? undefined : 'booking'
          authStore.setToken(token, expirationTime, tokenType, bookingNumber)
        }
      }
      return data
    } catch (jsonError) {
      console.error('Error parsing JSON response:', jsonError)
      return response.text().then((text) => {
        throw new Error(`Could not parse booking response: ${text.substring(0, 200)}${text.length > 200 ? '...' : ''}`)
      })
    }
  } catch (error) {
    console.error('Error creating booking:', error)
    localStorage.removeItem('pendingBooking')
    localStorage.removeItem('pendingBookingNumber')
    throw error
  }
}

export const updateSeatSelection = async (
  bookingNumber: string,
  seatData: {
    seats: Array<{
      carriage: string
      number: string
      item_ref?: number
      strategy?: string
      seat_preferences?: Array<{
        code: string
        preference: string
      }>
    }>
  }
): Promise<any> => {
  console.log('[SEAT_DEBUG] updateSeatSelection API function called with:', {
    bookingNumber,
    seatData
  });
  const errorStore = useErrorStore()
  const token = await getToken(false, true) // Use booking token
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v2/booking/${bookingNumber}/seats`

  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(seatData),
    })

    if (!response.ok) {
      const error = await response.json()
      console.error('Error updating seats:', {
        status: response.status,
        statusText: response.statusText,
        error,
        requestData: seatData,
      })
      throw new Error(error.message || 'Failed to update seats')
    }

    return await response.json()
  } catch (error) {
    console.error('Error updating seats:', error)
    errorStore.setError('Kunne ikke oppdatere setevalg')
    throw error
  }
}

export const submitCustomerInfo = async (
  bookingNumber: string,
  customerData: any
) => {
  const errorStore = useErrorStore()
  const token = await getToken(false, true)
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v2/booking/${bookingNumber}/customer`

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(customerData),
    })

    if (!response.ok) {
      const errorData = await response.json()
      console.error('Error response:', errorData)
      const error =
        'Kunne ikke oppdatere kundeinformasjon. Vennligst prøv igjen.'
      errorStore.setError(error)
      throw new Error(error)
    }

    return await response.json()
  } catch (error) {
    console.error('Submission error:', error)
    throw error
  }
}

export const updatePassengerInfo = async (
  bookingNumber: string,
  passengers: any[]
) => {
  const errorStore = useErrorStore()
  const token = await getToken(false, true)
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v2/booking/${bookingNumber}/passengers`

  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ passengers }),
    })

    if (!response.ok) {
      const error =
        'Kunne ikke oppdatere passasjerinformasjon. Vennligst prøv igjen.'
      errorStore.setError(error)
      throw new Error(error)
    }

    return await response.json()
  } catch (error) {
    console.error('Error updating passenger info:', error)
    throw error
  }
}

export const fetchPaymentMethods = async (
  bookingNumber: string,
  providedToken?: string
): Promise<any> => {
  const errorStore = useErrorStore()
  const userStore = useUserStore()

  if (!bookingNumber || typeof bookingNumber !== 'string') {
    const error = 'Invalid booking number'
    console.error(error, bookingNumber)
    errorStore.setError(error)
    throw new Error(error)
  }

  try {
    let token = providedToken
    if (!token) {
      if (userStore.isLoggedIn) {
        const pendingPayment = localStorage.getItem('pendingPayment')
        let storedBookingToken = null
        let storedBookingNumber = null

        if (pendingPayment) {
          const paymentData = JSON.parse(pendingPayment)
          storedBookingToken = paymentData.bookingToken
          storedBookingNumber = paymentData.bookingNumber
        }

        token =
          storedBookingNumber === bookingNumber && storedBookingToken
            ? storedBookingToken
            : await getToken(false, true)
      } else {
        const pendingPayment = localStorage.getItem('pendingPayment')
        if (pendingPayment) {
          const paymentData = JSON.parse(pendingPayment)
          if (paymentData.bookingNumber === bookingNumber) {
            token = paymentData.bookingToken
          }
        }
        if (!token) {
          token = await getToken()
        }
      }
    } else {
    }

    const headers = await getDefaultHeaders(token || undefined)
    const url = `${BASE_URL}/api/v2/payment/methods/${bookingNumber}`

    const response = await fetch(url, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json()
      const error =
        errorData.error?.message || 'Kunne ikke hente betalingsmetoder.'
      console.error('Payment methods fetch failed:', {
        status: response.status,
        errorData,
      })
      errorStore.setError(error)
      throw new Error(error)
    }

    return await response.json()
  } catch (error) {
    console.error('Error fetching payment methods:', error)
    throw error
  }
}

export const submitPayment = async (
  arg1: string | PaymentData,
  arg2?: string
): Promise<any> => {
  const errorStore = useErrorStore()
  const userStore = useUserStore()

  let paymentData: PaymentData

  // Handle single object (VIPPS)
  if (typeof arg1 === 'object') {
    paymentData = arg1
  }
  // Handle two arguments (legacy non-VIPPS)
  else if (typeof arg1 === 'string' && typeof arg2 === 'string') {
    paymentData = {
      booking_number: arg1,
      payment_method_code: arg2,
    }
  } else {
    const error = 'Invalid arguments for submitPayment'
    errorStore.setError(error)
    throw new Error(error)
  }

  try {
    let token
    if (userStore.isLoggedIn) {
      const storedBookingToken = localStorage.getItem('token')
      const storedBookingNumber = localStorage.getItem('pendingBookingNumber')
      
      token =
      storedBookingNumber === paymentData.booking_number && storedBookingToken
      ? storedBookingToken
      : await getToken(false, true)
    } else {
      const storedToken = localStorage.getItem('token');
      token = storedToken ? storedToken : await getToken(false, true);
    }

    const headers = await getDefaultHeaders(token || undefined)
    const url = `${BASE_URL}/api/v2/payment`

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(paymentData),
    })

    if (!response.ok) {
      const errorData = await response.json()
      const error =
        errorData.messages?.[0]?.content ||
        'Kunne ikke sende betalingsforespørsel.'
      console.error('submitPayment failed:', {
        status: response.status,
        errorData,
      })
      errorStore.setError(error)
      throw new Error(error)
    }

    return await response.json()
  } catch (error) {
    console.error('Error submitting payment:', error)
    throw error
  }
}

export const fetchBookingData = async (
  bookingNumber: string,
  isMyPage: boolean = false,
  excludeCancelledItems: boolean = true,
  excludeExpiredItems: boolean = true,
  providedToken?: string
): Promise<{ data: { booking: BookingData } }> => {
  const errorStore = useErrorStore()
  const authStore = useAuthStore()

  try {
    let token = providedToken
    if (!token) {
      const isRebooking = !isMyPage && 
        (window.location.pathname.includes('/after-sale') || 
         window.location.pathname.includes('/rebook') ||
         (window.location.search && window.location.search.includes('rebookingType')))
      
      if (isRebooking) {
        try {
          // First try to get a valid booking token through our specialized function
          token = await ensureValidBookingToken(bookingNumber)
        } catch (tokenError) {
          // Fall through to legacy token handling if that fails
        }
      }

      // Fallback to existing token in auth store if available
      if (
        !token &&
        authStore.isBookingToken &&
        authStore.currentBookingNumber === bookingNumber
      ) {
        token = authStore.token || undefined
      }
      // If still no token, try storage locations
      else if (!token) {
        // Try to get token from pendingPayment
        const pendingPayment = localStorage.getItem('pendingPayment')
        let storedBookingToken: string | undefined = undefined
        let storedBookingNumber: string | undefined = undefined

        if (pendingPayment) {
          try {
            const paymentData = JSON.parse(pendingPayment)
            storedBookingToken = paymentData.bookingToken
            storedBookingNumber = paymentData.bookingNumber
          } catch (e) {
            console.warn('Failed to parse pendingPayment data:', e)
          }
        }

        if (storedBookingNumber === bookingNumber && storedBookingToken) {
          token = storedBookingToken
        } else {
          // Try direct localStorage values
          storedBookingToken = localStorage.getItem('token') || undefined
          storedBookingNumber =
            localStorage.getItem('pendingBookingNumber') || undefined

          if (storedBookingNumber === bookingNumber && storedBookingToken) {
            token = storedBookingToken
          } else {
            // Last resort - get a public token for MyPage or a booking token otherwise
            token = await getToken(false, !isMyPage)
          }
        }
      }
    }

    const headers = await getDefaultHeaders(token || undefined)
    let url = `${BASE_URL}/api/v2/booking/${bookingNumber}`
    const excludeParams = []
    if (excludeCancelledItems) excludeParams.push('exclude[]=cancelled_items')
    if (excludeExpiredItems) excludeParams.push('exclude[]=expired_items')
    if (excludeParams.length > 0) url += `?${excludeParams.join('&')}`

    const response = await fetch(url, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      // Special handling for authentication errors during rebooking
      if ((response.status === 401 || response.status === 403) && !isMyPage) {
        // One last attempt to refresh the token
        try {
          const refreshed = await authStore.refreshBookingToken()
          if (refreshed) {
            // Retry with new token
            const newToken = authStore.token
            const newHeaders = await getDefaultHeaders(newToken || undefined)
            const retryResponse = await fetch(url, {
              method: 'GET',
              headers: newHeaders,
            })

            if (retryResponse.ok) {
              const data = await retryResponse.json()
              if (data && data.data && data.data.booking) {
                errorStore.clearError()
                return data
              }
            }
          }
        } catch (refreshError) {
          console.error(
            'Failed to refresh token during error recovery:',
            refreshError
          )
        }

        // If we get here, both attempts failed with auth errors
        const errorMessage = 'Din økt har utløpt. Vennligst start på nytt.'
        errorStore.setError(errorMessage)
        throw new Error(errorMessage)
      }

      // Handle other error types
      const errorText = await response.text()
      console.error('Booking fetch failed:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
        bookingNumber,
      })
      let errorMessage = 'Kunne ikke hente bestillingen.'
      if (response.status === 404) {
        errorMessage = 'Kunne ikke finne bestillingen.'
      }
      errorStore.setError(errorMessage)
      throw new Error(errorMessage)
    }

    const data = await response.json()
    if (!data || !data.data || !data.data.booking) {
      const error = 'Ugyldig bestillingsdata mottatt fra serveren.'
      errorStore.setError(error)
      throw new Error(error)
    }
    
    return data
  } catch (error) {
    console.error('Error fetching booking data:', error)
    throw error
  }
}

export const fetchCarriageLayout = async (
  serviceName: string,
  fromStation: string,
  toStation: string,
  travelDate: string
): Promise<CarriageLayout> => {
  const errorStore = useErrorStore()
  const token = await getToken()
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v2/meta/carriage-layouts/${serviceName}/${fromStation}/${toStation}/${travelDate}`

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const error = 'Kunne ikke hente vognoppsett.'
      errorStore.setError(error)
      throw new Error(error)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching carriage layout:', error)
    throw error
  }
}

export const searchServices = async (
  serviceName: string,
  date: string
): Promise<any> => {
  const errorStore = useErrorStore()
  const token = await getToken()
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v2/orientation/search-services?service_name=${serviceName}&date=${date}`

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const error = 'Kunne ikke søke etter tjenester.'
      errorStore.setError(error)
      throw new Error(error)
    }

    return await response.json()
  } catch (error) {
    console.error('Error searching services:', error)
    throw error
  }
}

export const fetchCalendarData = async (
  origin: string,
  destination: string,
  begin: string,
  end: string,
  isReturn: boolean = false
): Promise<any> => {
  const errorStore = useErrorStore()
  const token = await getToken()
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
  const url = `${BASE_URL}/api/v2/orientation/journey-search/calendar`

  const body = {
    currency: 'NOK',
    is_aftersales: false,
    passengers: [
      {
        type: 'ADT',
        id: 'passenger_1',
      },
    ],
    calendars: [
      {
        id: 'travel_outbound',
        origin: isReturn ? destination : origin,
        destination: isReturn ? origin : destination,
        entries: {
          begin,
          end,
        },
        direction: isReturn ? 'inbound' : 'outbound',
      },
    ],
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const error = await response.json()
      console.error('Error response from calendar API:', error)
      const errorMessage = 'Kunne ikke hente tilgjengelige datoer.'
      errorStore.setError(errorMessage)
      throw new Error(errorMessage)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching calendar dates:', error)
    throw error
  }
}

export const searchProducts = async (
  serviceIdentifier: string,
  direction: string,
  originStationCode: string,
  destinationStationCode: string,
  _departureDate: string,
  includedProductTypes?: Array<{ code: string }>
): Promise<any> => {
  const errorStore = useErrorStore()
  const token = await getToken()
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v3/orientation/search-products-with-services`

  const body: any = {
    currency_code: 'NOK',
    direction: direction.toUpperCase(),
    passengers: [
      {
        client_ref: 'passenger_1',
        type_code: 'ADT',
      },
    ],
    segment: {
      origin_station_code: originStationCode,
      destination_station_code: destinationStationCode,
      service_identifier: serviceIdentifier,
    },
  }

  if (includedProductTypes?.length) {
    body.included_product_types = includedProductTypes
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('Failed to search products:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
      })
      const error = 'Kunne ikke søke etter produkter.'
      errorStore.setError(error)
      throw new Error(error)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error searching products:', error)
    throw error
  }
}

export const updateBooking = async (bookingNumber: string, body: any) => {
  const errorStore = useErrorStore()
  const token = await getToken(false, true)
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v2/booking/${bookingNumber}`

  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const error = 'Kunne ikke oppdatere bestilling.'
      errorStore.setError(error)
      throw new Error(error)
    }

    return await response.json()
  } catch (error) {
    console.error('Error updating booking:', error)
    throw error
  }
}

export const fetchCardTypes = async (): Promise<
  { label: string; value: string }[]
> => {
  const errorStore = useErrorStore()
  const token = await getToken()
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v2/meta/card-types`

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const error = 'Kunne ikke hente korttyper.'
      errorStore.setError(error)
      throw new Error(error)
    }

    const data = await response.json()
    return data.data.card_types.map((card: any) => ({
      label: card.name,
      value: card.code,
    }))
  } catch (error) {
    console.error('Error fetching card types:', error)
    throw error
  }
}

export const submitProducts = async (
  bookingNumber: string,
  segments: Array<{
    origin: string
    origin_name: string
    destination: string
    destination_name: string
    start_validity_date: string
    start_validity_time: string
    direction: string
    items: Array<{
      tariff_code: string
      passenger_id: string
    }>
    service_name: string
    service_identifier: string
  }>,
  passengers: Array<{
    id: string
    type: string
  }>,
  currency: string
): Promise<any> => {
  const errorStore = useErrorStore()
  const token = await getToken(false, true)
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v2/booking/${bookingNumber}`

  const body = {
    segments,
    passengers,
    currency,
  }

  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('Failed to submit products:', {
        status: response.status,
        statusText: response.statusText,
        errorText,
      })
      const error = 'Kunne ikke sende inn produkter.'
      errorStore.setError(error)
      throw new Error(error)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error submitting products:', error)
    throw error
  }
}

export const submitAdditionalProducts = async (
  bookingNumber: string,
  segments: BookingSegment[],
  passengers: Array<{
    id: string
    type: string
  }>,
  currency: string
): Promise<any> => {
  const errorStore = useErrorStore()
  const token = await getToken(false, true)
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v2/booking/${encodeURIComponent(bookingNumber)}`

  const body = {
    segments,
    passengers,
    currency,
  }

  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    })

    const data = await response.json()

    if (!response.ok) {
      const error = data.error?.message || 'Kunne ikke legge til produkter.'
      errorStore.setError(error)
      throw new Error(error)
    }

    // Clear any existing error on success
    errorStore.clearError()
    return data
  } catch (error) {
    console.error('Error submitting additional products:', error)
    throw error
  }
}

export const fetchBookingItems = async (
  bookingNumber: string
): Promise<any> => {
  const errorStore = useErrorStore()
  const token = await getToken(false, true) // Use CRM token and indicate booking has started
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v3/booking/${bookingNumber}/items/vat`

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const error = 'Kunne ikke hente bestillingsdetaljer.'
      errorStore.setError(error)
      throw new Error(error)
    }

    return await response.json()
  } catch (error) {
    console.error('Error fetching booking items VAT:', error)
    throw error
  }
}

export const cancelBooking = async (bookingNumber: string): Promise<void> => {
  const errorStore = useErrorStore()
  const token = await getToken(false, true)
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v2/booking/${bookingNumber}/cancel`

  const response = await fetch(url, {
    method: 'POST',
    headers,
  })

  if (!response.ok) {
    const error = 'Kunne ikke avbestille bestilling.'
    errorStore.setError(error)
    throw new Error(error)
  }
}

export const processPayment = async (
  bookingNumber: string,
  paymentReference: string,
  token?: string,
  processData?: { payment_session: string }
): Promise<any> => {
  const errorStore = useErrorStore()
  const authToken = token || (await getToken(false, true))
  const headers = await getDefaultHeaders(authToken || undefined)
  const url = `${BASE_URL}/api/v2/payment/process`

  const defaultPayload = {
    booking_number: bookingNumber,
    payment_reference: paymentReference,
  }

  const payload = processData
    ? { ...defaultPayload, ...processData }
    : defaultPayload

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    })

    const responseBody = await response.json()

    if (!response.ok) {
      const errorMessage =
        responseBody.messages?.[0]?.content ||
        responseBody.error?.message ||
        'Kunne ikke fullføre betaling'
      console.error('processPayment failed:', {
        status: response.status,
        responseBody,
      })
      errorStore.setError(errorMessage)
      throw new Error(errorMessage)
    }

    return responseBody
  } catch (error: any) {
    console.error('Error processing payment:', error)
    errorStore.setError(error.message || 'Unknown error in payment processing')
    throw error
  }
}

export const verify3DSecurePayment = async (
  bookingNumber: string,
  paymentReference: string,
  redirectResult: string
): Promise<any> => {
  const errorStore = useErrorStore()
  const token = await getToken(false, true)
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v2/payment/verify-3ds`

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        booking_number: bookingNumber,
        payment_reference: paymentReference,
        redirect_result: redirectResult,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json()
      const error =
        errorData.messages?.[0]?.content || '3D Secure-verifisering mislyktes'
      console.error('verify3DSecurePayment failed:', {
        status: response.status,
        errorData,
      })
      errorStore.setError(error)
      throw new Error(error)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error verifying 3D Secure payment:', error)
    throw error
  }
}

export const fetchAfterSalesProfile = async (
  bookingNumber: string
): Promise<any> => {
  try {
    const token = await getToken(false, true)
    const headers = await getDefaultHeaders(token || undefined)
    const response = await fetch(
      `${BASE_URL}/api/v3/booking/${bookingNumber}/aftersales-profile`,
      {
        method: 'GET',
        headers,
      }
    )
    if (!response.ok) {
      throw new Error(
        `Failed to fetch after sales profile: ${response.status} ${response.statusText}`
      )
    }
    return await response.json()
  } catch (error) {
    console.error('Error fetching after sales profile:', error)
    throw error
  }
}

export const rebookSearch = async (
  bookingNumber: string,
  body: {
    intention: 'rebook' | 'upgrade'
    passengers: Array<{
      id: string
      discount_cards?: Array<{
        code: string
        identifier?: string
        validity_rules?: Array<{
          validity_start_date: string
          validity_end_date: string
          validity_segment?: {
            origin: string
            destination: string
          }
        }>
      }>
    }>
    replace_item_ids: string[]
    travels: Array<{
      direction: 'inbound' | 'outbound'
      departure: string
      destination: string
      origin: string
      product_types?: string[]
    }>
  }
): Promise<any> => {
  const errorStore = useErrorStore()
  const authStore = useAuthStore()

  try {
    let token: string | null = null;
    
    if (authStore.isBookingToken && 
        authStore.currentBookingNumber === bookingNumber &&
        authStore.tokenExpirationTime && 
        Date.now() < authStore.tokenExpirationTime - 5 * 60 * 1000) {
      token = authStore.token;
    }
    
    if (!token) {
      try {
        const refreshed = await authStore.refreshBookingToken();
        if (refreshed) {
          token = authStore.token;
        }
      } catch (refreshError) {
      }
    }
    if (!token) {
      token = await authStore.findAndUseEmailForBooking(bookingNumber);
    }
    if (!token) {
      try {
        token = await getToken(false, true);
      } catch (tokenError) {
        const error = 'Kunne ikke hente token for ombooking.';
        errorStore.setError(error);
        throw new Error(error);
      }
    }

    const headers = await getDefaultHeaders(token);
    const url = `${BASE_URL}/api/v2/orientation/booking/${bookingNumber}/journey-search`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      // Check if it's an authentication error
      if (response.status === 401 || response.status === 403) {
        const error = 'Din økt har utløpt. Vennligst start på nytt.';
        errorStore.setError(error);
        throw new Error('Session expired during rebooking');
      } else {
        const error = 'Kunne ikke utføre søk for ombooking.';
        errorStore.setError(error);
        throw new Error(error);
      }
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error performing rebook search:', error);
    throw error;
  }
}

export const cancelItems = async (
  bookingNumber: string,
  itemIdsOrOptions: number[] | { item_ids: number[] }
): Promise<any> => {
  const errorStore = useErrorStore()
  const token = await getToken()
  const headers = await getDefaultHeaders(token || undefined)

  const url = `${BASE_URL}/api/v2/booking/${bookingNumber}/items/cancel`

  try {
    const timestamp =
      new Date()
        .toLocaleString('sv-SE', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZone: 'Europe/Oslo',
        })
        .replace(' ', 'T') + '+01:00'

    const itemIds = Array.isArray(itemIdsOrOptions)
      ? itemIdsOrOptions
      : itemIdsOrOptions.item_ids

    const requestBody = {
      cancel_item_refs: itemIds,
      cancel_timestamp: timestamp,
      override_aftersales_rules: false,
      override_aftersales_fees: false,
    }
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      const error = await response.json()
      errorStore.setError('Det oppstod en feil ved kansellering av billetter.')
      throw new Error(JSON.stringify(error))
    }

    const responseData = await response.json()
    if (responseData && responseData.booking) {
      return responseData
    }
  } catch (error) {
    console.error('[journeyService] Error cancelling items:', error)
    throw error
  }
}

export const confirmBooking = async (
  bookingNumber: string,
  options?: {
    triggerBookingCommunication?: boolean
    overrides?: {
      remoteCancellationError?: boolean
    }
  }
): Promise<any> => {
  const errorStore = useErrorStore()
  const token = await getToken(false, true)
  const headers = await getDefaultHeaders(token || undefined)

  const url = `${BASE_URL}/api/v2/booking/${bookingNumber}/confirm`

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        trigger_booking_communication:
          options?.triggerBookingCommunication ?? false,
        overrides: options?.overrides
          ? {
              remote_cancellation_error:
                options.overrides.remoteCancellationError,
            }
          : undefined,
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      errorStore.setError(
        'Det oppstod en feil ved bekreftelse av bestillingen.'
      )
      throw new Error(JSON.stringify(error))
    }

    return await response.json()
  } catch (error) {
    console.error('Error confirming booking:', error)
    throw error
  }
}

export const requestRefund = async (
  bookingNumber: string,
  type: 'voucher' | 'psp' | 'manual',
  pspDetail?: {
    payment_reference: string
    refund_reference: string
    amount: number
  },
  pspFallback?: 'NONE' | 'ANY'
): Promise<any> => {
  const errorStore = useErrorStore()
  try {
    const token = await getToken()
    const headers = await getDefaultHeaders(token || undefined)
    const response = await fetch(`${BASE_URL}/api/v2/request-refund`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        booking_number: bookingNumber,
        type,
        ...(pspDetail && { psp_detail: pspDetail }),
        ...(pspFallback && { psp_fallback: pspFallback }),
      }),
    })

    if (!response.ok) {
      const errorData = await response.json()
      console.error('Refund request failed:', errorData)
      errorStore.setError('Det oppstod en feil ved behandling av refusjon.')
      throw new Error(JSON.stringify(errorData))
    }

    return await response.json()
  } catch (error) {
    console.error('Error requesting refund:', error)
    throw error
  }
}

export const rebookItems = async (
  bookingNumber: string,
  body: {
    item_adjustments: {
      cancel_item_ids?: string[]
      replacements: Array<{
        added_item_ref_ids: string[]
        existing_item_ids: string[]
      }>
    }
    passengers?: Array<{
      ref_id?: string
      uuid?: string
      discount_cards?: Array<{
        code: string
        identifier?: string
        validity_rules?: Array<{
          validity_start_date: string
          validity_end_date: string
          validity_segment?: {
            origin_station_code: string
            destination_station_code: string
          }
        }>
      }>
      travel_passes?: Array<{
        code: string
        role?: 'NON_PERSONAL' | 'HOLDER' | 'FELLOW_TRAVELLER'
      }>
    }>
    segments?: Array<{
      schedule_date: string
      origin_station_code?: string
      destination_station_code?: string
      direction?: 'OUTBOUND' | 'INBOUND'
      service_identifier?: string
      service_name?: string
      service_type?: string
      tariff_group_code?: string
      is_international?: boolean
      ref_id?: string
    }>
    items?: Array<{
      ref_id?: string
      client_ref?: string
      passenger_ref_id?: string
      segment_ref_id?: string
      tariff_code?: string
      price?: number
      ticket_number?: string
      seat?: {
        carriage?: string
        number?: string
        strategy?: 'EXACT' | 'CLOSE_TO'
      }
      seat_preferences?: Array<any>
      conditions?: {
        applied_regulatory_conditions?: string
      }
    }>
    journeys?: Array<{
      id?: string
      segment_ref_ids?: string[]
    }>
    overrides?: {
      aftersales_fees?: boolean
      aftersales_rules?: boolean
      validation_rules?: string[]
      booking_limits?: boolean
      remote_cancellation_error?: boolean
    }
    promos?: Array<{
      code: string
    }>
    reason?: string
    sales_channel_code?: string
    shift_id?: number
    affiliate_code?: string
    currency?: string
  }
): Promise<any> => {
  const errorStore = useErrorStore()
  // Use CRM token for after-sales operations
  const token = await getToken(false, false)
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v3/booking/${bookingNumber}/rebook-items`

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const errorData = await response.json()
      console.error('Rebook API error:', {
        status: response.status,
        statusText: response.statusText,
        errorData,
      })
      
      // Log detailed error information
      if (errorData.errors && errorData.errors.length > 0) {
        console.error('Detailed API errors:', errorData.errors)
        
        // Extract specific error messages and source pointers
        const errorMessages = errorData.errors.map((err: any) => {
          let message = `${err.title || ''}: ${err.detail || ''}`;
          if (err.source && err.source.pointer) {
            message += ` (at ${err.source.pointer})`;
          }
          return message;
        }).join('\n')
        
        console.error('Error messages:', errorMessages)
        
        // Log each error separately for better debugging
        errorData.errors.forEach((err: any, index: number) => {
          console.error(`API Error ${index + 1}:`, {
            title: err.title,
            detail: err.detail,
            source: err.source,
            code: err.code,
            status: err.status
          });
        });
        
        // Set a more specific error message if available
        if (errorMessages) {
          errorStore.setError(`Kunne ikke endre reisen: ${errorMessages}`)
          throw new Error(`Kunne ikke endre reisen: ${errorMessages}`)
        }
      }

      const error = errorData.message || 'Kunne ikke endre reisen.'
      errorStore.setError(error)
      throw new Error(error)
    }

    if (response.status === 204) {
      return { status: 204 }
    }

    return await response.json()
  } catch (error) {
    console.error('Error rebooking items:', error)
    throw error
  }
}

export const submitCardPayment = async (paymentData: {
  booking_number: string
  payment_method_code: string
  currency_code: string
  amount: number
  encrypted_card_number: string
  encrypted_expiry_month: string
  encrypted_expiry_year: string
  encrypted_security_code: string
  billing_address?: {
    country_code: string
    city: string
    street: string
    house_number: string
    postal_code: string
  }
}): Promise<any> => {
  const errorStore = useErrorStore()
  const token = localStorage.getItem('token') ? localStorage.getItem('token') : await getToken(false, true);
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v2/payment`

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(paymentData),
    })

    if (!response.ok) {
      const errorData = await response.json()
      const error = errorData.messages?.[0]?.content || 'Betaling mislyktes'
      console.error('submitCardPayment failed:', {
        status: response.status,
        errorData,
      })
      errorStore.setError(error)
      throw new Error(error)
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error submitting card payment:', error)
    throw error
  }
}

export const searchTravelOptions = async (
  origin: string,
  destination: string,
  startDate: string,
  isReturn: boolean = false
): Promise<any> => {
  const errorStore = useErrorStore()
  const token = await getToken()
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
  const url = `${BASE_URL}/api/v3/navigation/search-travel-options`

  // Create a 2-day window
  const depart_after = `${startDate}T00:00:00Z`
  const nextDay = format(addDays(new Date(startDate), 1), 'yyyy-MM-dd')
  const depart_before = `${nextDay}T23:59:59Z`

  const body = {
    travel_path: [
      { stop_code: isReturn ? destination : origin },
      { stop_code: isReturn ? origin : destination },
    ],
    direction: isReturn ? 'INBOUND' : 'OUTBOUND',
    depart_after_datetime: depart_after,
    depart_before_datetime: depart_before,
    sort_by: 'DEPARTURE_DATETIME',
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const error = 'Kunne ikke hente reisealternativer.'
      errorStore.setError(error)
      throw new Error(error)
    }

    const data = await response.json()
    return {
      data: {
        travel_options: data.travel_options || [],
      },
    }
  } catch (error) {
    console.error('Error fetching travel options:', error)
    throw error
  }
}

export const deleteItems = async (
  bookingNumber: string,
  itemIds: string[]
): Promise<any> => {
  const errorStore = useErrorStore()
  const token = await getToken(false, true)
  const headers = await getDefaultHeaders(token || undefined)
  const url = `${BASE_URL}/api/v2/booking/${bookingNumber}/items`

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ item_ids: itemIds }),
    })

    if (!response.ok) {
      const error = 'Kunne ikke slette produkter.'
      errorStore.setError(error)
      throw new Error(error)
    }

    return await response.json()
  } catch (error) {
    console.error('Error deleting items:', error)
    throw error
  }
}

export const addVoucher = async (
  bookingNumber: string,
  voucherCode: string
): Promise<any> => {
  const errorStore = useErrorStore()
  const userStore = useUserStore()

  try {
    let token
    if (userStore.isLoggedIn) {
      const storedBookingToken = localStorage.getItem('token')
      const storedBookingNumber = localStorage.getItem('pendingBookingNumber')

      token =
        storedBookingNumber === bookingNumber && storedBookingToken
          ? storedBookingToken
          : await getToken(false, true)
    } else {
      token = await getToken()
    }

    const headers = await getDefaultHeaders(token || undefined)
    const url = `${BASE_URL}/api/v2/payment/voucher`

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        booking_number: bookingNumber,
        voucher_code: voucherCode,
      }),
    })

    if (!response.ok) {
      const error = 'Kunne ikke legge til rabattkode.'
      errorStore.setError(error)
      throw new Error(error)
    }

    return await response.json()
  } catch (error) {
    console.error('Error adding voucher:', error)
    throw error
  }
}

export const revertBooking = async (bookingNumber: string): Promise<any> => {
  const errorStore = useErrorStore()
  const token = await getToken()
  const headers = await getDefaultHeaders(token)

  try {
    const response = await fetch(
      `${BASE_URL}/api/v2/booking/${bookingNumber}/revert`,
      {
        method: 'POST',
        headers,
      }
    )

    if (!response.ok) {
      const error = 'Could not revert booking changes.'
      errorStore.setError(error)
      throw new Error(error)
    }

    return await response.json()
  } catch (error) {
    console.error('Error reverting booking:', error)
    throw error
  }
}

export const patchProducts = async (
  bookingNumber: string,
  products: Array<{
    product_code: string
    service_identifier: string
    passenger_id: string
    tariff_code: string
  }>
): Promise<any> => {
  const response = await fetch(
    `${BASE_URL}/api/v2/booking/${bookingNumber}/patch-products`,
    {
      method: 'POST',
      headers: await getDefaultHeaders(await getToken()),
      body: JSON.stringify({
        products: products.map((product) => ({
          product_code: product.product_code,
          service_identifier: product.service_identifier,
          passenger_id: product.passenger_id,
          tariff_code: product.tariff_code,
        })),
      }),
    }
  )
  return await response.json()
}

export const cancelProvisionalBooking = async (
  bookingNumber: string,
  silentMode: boolean = false
): Promise<void> => {
  if (!bookingNumber) {
    console.log('Cannot cancel booking: No booking number provided')
    return
  }

  const errorStore = useErrorStore()
  
  try {
    console.log('Proceeding with cancellation of booking:', bookingNumber)
    
    const token = await getToken()
    const headers = await getDefaultHeaders(token)
    
    const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/api/v2/booking/${bookingNumber}/cancel`
    
    const now = new Date()
    const formattedTimestamp = now.toISOString().replace(/\.\d+Z$/, 'Z')
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        cancel_timestamp: formattedTimestamp,
      }),
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      // Only log to console, don't set global error if in silent mode
      if (silentMode) {
        console.log('Silent failure to cancel provisional booking:', errorData)
      } else {
        console.error('Failed to cancel provisional booking:', errorData)
        errorStore.setError(`Failed to cancel provisional booking: ${errorData.message || response.statusText}`)
      }
      throw new Error(`Failed to cancel provisional booking: ${response.statusText}`)
    }
    
    clearProvisionalBookingInfo()
  } catch (error) {
    if (silentMode) {
      console.log('Silent error cancelling provisional booking:', error)
    } else {
      console.error('Error cancelling provisional booking:', error)
    }
    throw error
  }
}

export const cancelProvisionalBookingLegacy = async (
bookingNumber: string
): Promise<boolean> => {
const errorStore = useErrorStore()
const token = await getToken()
const headers = await getDefaultHeaders(token)
const url = `${BASE_URL}/api/v2/booking/${bookingNumber}/cancel`

try {
const response = await fetch(url, {
method: 'POST',
headers,
body: JSON.stringify({
cancel_timestamp: new Date().toISOString()
}),
})

if (!response.ok) {
const error = 'Could not cancel provisional booking.'
errorStore.setError(error)
throw new Error(error)
}

return true 
} catch (error) {
console.error('Error cancelling provisional booking:', error)
throw error
}
}
